import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, Sparkles, Rocket } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { useAuth } from "@/context/AuthContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { formatUSD } from "@/lib/constants";
import { useTranslation } from "react-i18next";

const TYPES = [
  { g: "Website", items: [["business_website", "Business Website"], ["corporate_website", "Corporate Website"], ["premium_website", "Premium Website"], ["ecommerce_website", "E-commerce Website"], ["landing_page", "Landing Page"], ["website_redesign", "Website Redesign"]] },
  { g: "Mobile", items: [["android_app", "Android App"], ["ios_app", "iOS App"], ["cross_platform_app", "Cross-Platform App"], ["mobile_mvp", "Mobile MVP"]] },
  { g: "Web Application", items: [["web_application", "Web Application"], ["customer_portal", "Customer Portal"], ["admin_dashboard", "Admin Dashboard"], ["saas_mvp", "SaaS MVP"], ["marketplace", "Marketplace"]] },
  { g: "Custom", items: [["custom_software", "Custom Software"]] },
];
const TYPE_LABEL = Object.fromEntries(TYPES.flatMap((t) => t.items));
const FEATURES = [["cms", "CMS"], ["admin_panel", "Admin Panel"], ["user_login", "User Login"], ["payment", "Payment"], ["api", "API"], ["database", "Database"], ["ai", "AI Integration"], ["ecommerce", "E-commerce"], ["notifications", "Notifications"], ["analytics", "Analytics"]];
const TIMELINES = ["Flexible", "1–2 months", "2–3 months", "3+ months"];

export default function DigitalStudio() {
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const typeL = (k) => tr(`studio.type.${k}`, { defaultValue: TYPE_LABEL[k] });
  const featL = (k, l) => tr(`studio.feat.${k}`, { defaultValue: l });
  const { user } = useAuth();
  const { config } = useSiteConfig();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [type, setType] = useState("");
  const [feats, setFeats] = useState([]);
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState({ name: "", email: "", phone: "", message: "" });

  const base = config?.studio_base || {};
  const fp = config?.studio_features || {};

  useEffect(() => {
    const t = params.get("type"); if (t && TYPE_LABEL[t]) { setType(t); setStep(2); }
    const f = params.get("features"); if (f) setFeats(f.split(",").filter((x) => fp[x] !== undefined));
    // eslint-disable-next-line
  }, [config]);

  const items = useMemo(() => {
    const list = [];
    if (type) list.push({ label: typeL(type), amount: Number(base[type] || 0) });
    feats.forEach((k) => list.push({ label: featL(k, FEATURES.find((x) => x[0] === k)?.[1] || k), amount: Number(fp[k] || 0) }));
    return list;
  }, [type, feats, base, fp, tr]);
  const total = items.reduce((s, i) => s + i.amount, 0);
  const low = Math.round(total * 0.95);
  const high = Math.round(total * 1.08);
  const toggleFeat = (k) => setFeats((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]));

  const checkout = async () => {
    if (!type) return toast.error(tr("studio.chooseType"));
    if (!user) { toast.message(tr("studio.signIn")); return navigate("/login"); }
    setBusy(true);
    try {
      const res = await api.post("/requests", {
        custom_name: `Digital Studio — ${TYPE_LABEL[type]}`,
        custom_category: "Digital Studio",
        custom_items: items.map((it, i) => ({ ...it, label: i === 0 ? TYPE_LABEL[type] : (FEATURES.find((x) => x[0] === feats[i - 1])?.[1] || it.label) })),
        company_name: `Digital Studio — ${TYPE_LABEL[type]}`,
        contact_name: user.name || "Client", contact_email: user.email,
        meta: { type, features: feats, budget, timeline, estimate: [low, high] },
      });
      const co = await api.post("/payments/checkout", { request_id: res.data.id, origin_url: window.location.origin });
      trackEvent("begin_checkout", { package_name: `Digital Studio — ${TYPE_LABEL[type]}`, amount: Math.round(total * 100) / 100, currency: "usd" });
      try { localStorage.setItem("uni_pending_purchase", JSON.stringify({ session_id: co.data.session_id, amount: Math.round(total * 100) / 100, currency: "usd" })); } catch { /* noop */ }
      window.location.href = co.data.url;
    } catch { toast.error(tr("studio.checkoutErr")); setBusy(false); }
  };

  const sendQuote = async () => {
    if (!quote.name.trim() || !quote.email.includes("@")) return toast.error(tr("studio.nameEmail"));
    setBusy(true);
    try {
      const summary = `Digital Studio quote request\nProject: ${type ? TYPE_LABEL[type] : "-"}\nFeatures: ${feats.join(", ") || "-"}\nBudget: ${budget || "-"}\nTimeline: ${timeline || "-"}\nEstimate: ${total ? `${formatUSD(low)}–${formatUSD(high)}` : "-"}\n\n${quote.message}`;
      await api.post("/contact", { name: quote.name, email: quote.email, phone: quote.phone, company: "Digital Studio", service: "Digital Studio", message: summary, type: "contact" });
      toast.success(tr("studio.sent"));
      setQuote({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error(tr("studio.sendErr")); } finally { setBusy(false); }
  };

  const Section = ({ n, title, children }) => (
    <div className={`rounded-sm border p-6 card-soft transition-all ${step === n ? "border-orange-300 bg-card" : "border-border bg-card/60"}`} data-testid={`studio-step-${n}`}>
      <button onClick={() => setStep(n)} className="flex items-center gap-2 w-full text-left"><span className={`grid place-items-center h-6 w-6 rounded-sm text-xs font-mono ${step >= n ? "bg-orange-600 text-white" : "bg-secondary text-muted-foreground"}`}>{n}</span><span className="font-heading text-lg text-primary">{title}</span></button>
      {step === n && <div className="mt-5">{children}</div>}
    </div>
  );
  const chip = (active) => `rounded-sm border px-3.5 py-2 text-sm text-left transition-colors ${active ? "border-orange-400 bg-orange-50 text-orange-700" : "border-border bg-card text-slate-600 hover:border-orange-300"}`;

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain pt-32 pb-12 border-b border-border bg-primary text-primary-foreground">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-orange-300 font-mono flex items-center gap-2"><Rocket className="h-3.5 w-3.5" /> {tr("studio.kicker")}</p>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight">{tr("studio.h1")}</h1>
          <p className="mt-4 text-primary-foreground/75 max-w-2xl">{tr("studio.lead")}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 space-y-4">
          <Section n={1} title={tr("studio.s1")}>
            {TYPES.map((grp, gi) => (
              <div key={grp.g} className="mb-4">
                <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{tr(`studio.grp${gi}`)}</p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {grp.items.map(([k]) => (
                    <button key={k} onClick={() => { setType(k); setStep(2); }} className={chip(type === k)} data-testid={`studio-type-${k}`}>{typeL(k)}<span className="block text-[11px] font-mono opacity-70">{base[k] ? tr("studio.from", { amount: formatUSD(base[k]) }) : tr("studio.quote")}</span></button>
                  ))}
                </div>
              </div>
            ))}
          </Section>
          <Section n={2} title={tr("studio.s2")}>
            <div className="grid sm:grid-cols-3 gap-2">
              {FEATURES.map(([k, l]) => (
                <button key={k} onClick={() => toggleFeat(k)} className={chip(feats.includes(k))} data-testid={`studio-feat-${k}`}><span className="flex items-center justify-between">{featL(k, l)} {feats.includes(k) && <Check className="h-3.5 w-3.5" />}</span><span className="block text-[11px] font-mono opacity-70">+{formatUSD(fp[k] || 0)}</span></button>
              ))}
            </div>
            <div className="mt-4 flex justify-end"><Button size="sm" variant="outline" className="rounded-sm" onClick={() => setStep(3)}>{tr("studio.next")} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button></div>
          </Section>
          <Section n={3} title={tr("studio.s3")}>
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{tr("studio.budget")}</p>
            <div className="grid sm:grid-cols-3 gap-2">{["$500–$1,000", "$1,000–$2,500", "$2,500–$5,000", "$5,000–$10,000", "$10,000+"].map((b) => (<button key={b} onClick={() => setBudget(b)} className={chip(budget === b)} data-testid={`studio-budget-${b}`}>{b}</button>))}</div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mt-4 mb-2">{tr("studio.timeline")}</p>
            <div className="grid sm:grid-cols-4 gap-2">{TIMELINES.map((t, ti) => (<button key={t} onClick={() => setTimeline(t)} className={chip(timeline === t)} data-testid={`studio-timeline-${t}`}>{tr(`studio.tl${ti}`)}</button>))}</div>
          </Section>
        </div>

        {/* Estimate */}
        <div className="lg:sticky lg:top-28 rounded-sm border border-border bg-card p-6 card-soft" data-testid="studio-estimate">
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{tr("studio.estimate")}</p>
          {items.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">{tr("studio.pick")}</p>
          ) : (
            <>
              <div className="mt-3 space-y-1.5">
                {items.map((i, x) => (<div key={x} className="flex items-center justify-between text-sm"><span className="text-slate-600">{i.label}</span><span className="font-mono text-foreground">{i.amount ? formatUSD(i.amount) : tr("studio.quote")}</span></div>))}
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-2xl font-heading text-primary">{formatUSD(low)} – {formatUSD(high)}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{tr("studio.initial")}</p>
              </div>
              <Button onClick={checkout} disabled={busy} className="mt-4 w-full rounded-sm bg-orange-600 text-white hover:bg-orange-700 h-11" data-testid="studio-checkout">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{tr("studio.checkout")} <ArrowRight className="h-4 w-4 ml-1.5" /></>}</Button>
            </>
          )}
          <div className="mt-6 pt-5 border-t border-border">
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{tr("studio.prefer")}</p>
            <div className="space-y-2">
              <Input value={quote.name} onChange={(e) => setQuote({ ...quote, name: e.target.value })} placeholder={tr("studio.name")} className="bg-background border-border text-sm" data-testid="studio-quote-name" />
              <Input value={quote.email} onChange={(e) => setQuote({ ...quote, email: e.target.value })} placeholder={tr("studio.email")} className="bg-background border-border text-sm" data-testid="studio-quote-email" />
              <Input value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} placeholder={tr("studio.anything")} className="bg-background border-border text-sm" data-testid="studio-quote-message" />
              <Button onClick={sendQuote} disabled={busy} variant="outline" className="w-full rounded-sm h-10" data-testid="studio-quote-send">{tr("studio.send")}</Button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
